import React, { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { MdDelete, MdEdit, MdLink, MdVisibilityOff } from 'react-icons/md';
import Link from 'next/link';
import { DateTime } from 'luxon';
import {
  DeleteGroupDocument,
  EditGroupsTableQuery,
} from '@website/types/graphqlOperations';
import PriceFormat from '@website/components/utilities/PriceFormat';
import { useMutation } from '@apollo/client';
import ConfirmDialog from '../../../utilities/ConfirmDialog';
import sumSpacesBooked from './utils/sumSpacesBooked';

/**
 * Confirm delete group modal
 */
export function ConfirmDeleteGroupModal({
  group,
  visible,
  setVisible,
  startDateFormatted,
  endDateFormatted,
}: {
  group: Pick<EditGroupsTableQuery['trip']['groups']['items'][0], 'id'>;
  visible: boolean;
  setVisible: (v: boolean) => void;
  startDateFormatted: string;
  endDateFormatted: string;
}) {
  const [deleteGroupMutation] = useMutation(DeleteGroupDocument, {
    // Delete the Group from the cache
    update(cache) {
      const cacheID = cache.identify({
        __typename: 'Group',
        id: group.id,
      });
      cache.evict({ id: cacheID });
    },
  });

  return (
    <ConfirmDialog
      title="Delete Group?"
      body={`Are you sure you want to delete this group that starts on ${startDateFormatted} and ends on ${endDateFormatted}?`}
      visible={visible}
      setVisible={setVisible}
      onConfirmFn={() => deleteGroupMutation({ variables: { id: group.id } })}
    />
  );
}

/**
 * Calculate the days difference between two dates
 */
export function calculateDaysDifference(
  startDate: DateTime,
  endDate: DateTime,
): number {
  const startDayStart = startDate.startOf('day');
  const endDayStart = endDate.startOf('day');
  const diff = endDayStart.diff(startDayStart, 'days').toObject();
  return diff.days! + 1;
}

/**
 * Group Row (View Mode)
 *
 * Shows the group row in view mode (not edit mode).
 */
export function GroupRowView({
  group,
  toggleEditGroup,
  position,
}: {
  group: EditGroupsTableQuery['trip']['groups']['items'][0];
  toggleEditGroup: () => void;
  position: number;
}) {
  // Setup state for delete confirmation
  const [confirmDeleteIsVisible, showConfirmDeleteDialog] =
    useState<boolean>(false);

  // Group dates
  const startDate = DateTime.fromISO(group.start.utc, {
    zone: group.start.zone,
  });
  const startDateFormatted = startDate.toLocaleString(
    DateTime.DATE_MED_WITH_WEEKDAY,
  );
  const endDate = DateTime.fromISO(group.end.utc, {
    zone: group.end.zone,
  });
  const endDateFormatted = endDate.toLocaleString(
    DateTime.DATE_MED_WITH_WEEKDAY,
  );
  const daysDifference = calculateDaysDifference(startDate, endDate);

  // Group spaces booked
  const spacesBooked = sumSpacesBooked(group);

  // Row formatting background (for a striped table look)
  const alternativeRowBackground = position % 2 === 0 ? 'bg-light' : 'bg-white';

  return (
    <>
      <ConfirmDeleteGroupModal
        group={group}
        startDateFormatted={startDateFormatted}
        endDateFormatted={endDateFormatted}
        visible={confirmDeleteIsVisible}
        setVisible={showConfirmDeleteDialog}
      />
      <Row
        className={`border-bottom ${alternativeRowBackground} p-2 py-3 p-md-0 p-md-y-0`}
      >
        <Col className="p-1" md={2} xs={12}>
          <h5 className="d-block d-md-none fw-bold m-0">
            {startDateFormatted} - {endDateFormatted}
          </h5>
          <div className="d-none d-md-inline">
            {startDateFormatted}
            <p className="text-muted small mb-0">{endDateFormatted}</p>
          </div>
        </Col>
        <Col className="p-1" md={1} xs={12}>
          {daysDifference}
          <span className="d-md-none"> days</span>
        </Col>
        <Col className="p-1" md={2} xs={12}>
          {spacesBooked}/{spacesBooked + group.remainingSpaces}{' '}
          <span className="d-md-none">spaces booked</span>
          <p className="text-muted small mb-0">
            {group.remainingSpaces} remaining
          </p>
        </Col>

        <Col className="p-1" md={3} xs={12}>
          <span className="d-md-none">You receive </span>
          <PriceFormat
            amount={group.hostPrice.amountZD}
            currencyIso={group.hostPrice.currency}
          />
          <p className="text-muted small mb-0">
            Customer pays{' '}
            <PriceFormat
              amount={group.guestPrice.amountZD}
              currencyIso={group.guestPrice.currency}
            />
          </p>
        </Col>
        <Col className="p-1" md={1} xs={12}>
          {group.private && (
            <>
              <MdVisibilityOff />
              <span className="d-md-none"> Private</span>
            </>
          )}
        </Col>
        <Col className="p-1" md={1} xs={12}>
          <div className="d-none d-md-inline">
            {group.waitList.items.length}
          </div>
          {group.waitList.items.length > 0 && (
            <div className="d-inline d-md-none">
              {group.waitList.items.length} on wait list
            </div>
          )}
        </Col>
        <Col className="p-1" md={2} xs={12}>
          <Button
            variant="success"
            onClick={() => toggleEditGroup()}
            className="me-1"
          >
            <MdEdit />
          </Button>

          <Link href="/book/[groupId]" as={`/book/${group.id}`} legacyBehavior>
            <Button variant="secondary" className="me-1">
              <MdLink />
            </Button>
          </Link>

          {spacesBooked === 0 && (
            <>
              <Button
                variant="danger"
                aria-label="Delete"
                onClick={() => showConfirmDeleteDialog(true)}
              >
                <MdDelete />
              </Button>
            </>
          )}
        </Col>
      </Row>
    </>
  );
}
